import { useWebinarT } from '@/hooks/useWebinarT';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  CAM_FORMAT_CHOICES_BY_POSITION,
  CAM_POSITIONS,
  CAM_SIZES,
  fromCamLayoutSelection,
  toCamLayoutSelection,
  type CamFormatChoice,
  type CamLayoutConfig,
  type CamPosition,
  type CamSize,
} from '@/lib/camLayouts';
import { cn } from '@/lib/utils';

/**
 * Trio Position / Taille / Format de la mise en page caméras, partagé entre la configuration du
 * workspace et la roue crantée de la régie. Le format se restreint à la position (portraits
 * uniquement à gauche) et se fige en 4:3 pour le coin bas droit.
 */
export default function CamLayoutSelectors({
  value,
  onChange,
  size = 'default',
  className,
}: {
  value: CamLayoutConfig;
  onChange: (config: CamLayoutConfig) => void;
  size?: 'default' | 'sm';
  className?: string;
}) {
  const { t } = useWebinarT('common');
  const selection = toCamLayoutSelection(value);
  const formats = CAM_FORMAT_CHOICES_BY_POSITION[selection.position];
  const update = (patch: Partial<typeof selection>) =>
    onChange(fromCamLayoutSelection({ ...selection, ...patch }));
  const triggerClass = size === 'sm' ? 'h-11 min-w-0 px-3' : undefined;

  return (
    <div className={cn('grid grid-cols-1 gap-2 sm:grid-cols-3', className)}>
      <Select
        value={selection.position}
        onValueChange={(position) => update({ position: position as CamPosition })}
      >
        <SelectTrigger className={triggerClass} label={t('camLayout.position')}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {CAM_POSITIONS.map((position) => (
            <SelectItem key={position} value={position}>
              {t(`camLayout.position_${position}`)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select
        value={selection.size}
        onValueChange={(camSize) => update({ size: camSize as CamSize })}
      >
        <SelectTrigger className={triggerClass} label={t('camLayout.size')}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {CAM_SIZES.map((camSize) => (
            <SelectItem key={camSize} value={camSize}>
              {t(`camLayout.size_${camSize}`)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select
        value={selection.format}
        onValueChange={(format) => update({ format: format as CamFormatChoice })}
        disabled={formats.length <= 1}
      >
        <SelectTrigger className={triggerClass} label={t('camLayout.format')}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {formats.map((format) => (
            <SelectItem key={format} value={format}>
              {t(`camLayout.format_${format}`)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
